// DTO de D4 — Análisis por Cliente del directivo.
//
//   - GET /api/directivo/analisis-cliente?mes=YYYY-MM-DD
//
// Vista del eje Cliente (Alpina, Éxito, otros): volumen y participación,
// composición 6 meses, detalle por cliente. SOLO LECTURA.
//
// Margen por cliente y pipeline de clientes en negociación están DIFERIDOS a F2
// (requieren costos_linea/precios_sku cargados y un modelo de CRM/pipeline).
// El schema los expone como nullable para no fabricar números financieros.
import { z } from 'zod';
import { pendienteValidacionUtilidadSchema } from './coordinador_cumplimiento.js';
import { mesPuntoSchema } from './directivo_analisis_mensual.js';
export const directivoAnalisisClienteQuerySchema = z.object({
    mes: z.string().date().optional(),
});
// =============================================================================
// KPI por cliente (cards de participación)
// =============================================================================
export const clienteKpiSchema = z.object({
    cliente_id: z.string().uuid(),
    nombre: z.string(),
    /** % del volumen total del mes. */
    share_pct: z.number().min(0),
    cantidad: z.number().min(0),
    es_principal: z.boolean(),
});
// =============================================================================
// Composición 6 meses (área apilada)
// =============================================================================
export const composicionSchema = z.object({
    meses: z.array(mesPuntoSchema),
    clientes: z.array(z.object({ cliente_id: z.string().uuid(), nombre: z.string() })),
    puntos: z.array(z.object({ mes_iso: z.string(), cliente_id: z.string().uuid(), cantidad: z.number().min(0) })),
});
// =============================================================================
// Detalle por cliente
// =============================================================================
export const clienteDetalleSchema = z.object({
    cliente_id: z.string().uuid(),
    nombre: z.string(),
    skus_count: z.number().int().min(0),
    volumen: z.number().min(0),
    share_pct: z.number().min(0),
    /** Nombres de plantas que sirven al cliente. */
    plantas: z.array(z.string()),
    /** Cumplimiento vs programa. null si el cliente no tiene programación (ej. no-Alpina). */
    cumplimiento_pct: z.number().nullable(),
    /** Margen % del cliente, CONSOLIDADO líneas + tiendas (los insumos de tienda
     *  se cobran: suman al valor). null sin costos o sin permiso. */
    margen_pct: z.number().nullable(),
    /** Utilidad de líneas en pesos ($) del cliente. null sin costos o sin permiso. */
    utilidad: z.number().nullable(),
    /**
     * DN10: margen y utilidad van sobre producción VALIDADA; esto es lo capturado
     * sin validar del cliente (líneas + tiendas), declarado y NO sumado.
     */
    pendiente_validacion_utilidad: pendienteValidacionUtilidadSchema.nullable().optional(),
});
// =============================================================================
// Response
// =============================================================================
export const directivoAnalisisClienteSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    mes: z.object({ desde: z.string().date(), label: z.string() }),
    /** Capacidad "ver costos/rentabilidad" del rol/usuario. Gate del FE. */
    puede_ver_costos: z.boolean(),
    clientes_activos: z.number().int().min(0),
    /** KPIs de participación (top clientes del mes). */
    clientes_kpi: z.array(clienteKpiSchema),
    /** Clientes nuevos en el mes (alta reciente). */
    clientes_nuevos: z.number().int().min(0),
    /** Clientes en pipeline de negociación. null = no modelado aún (F2). */
    pipeline_count: z.number().int().nullable(),
    composicion: composicionSchema,
    detalle: z.array(clienteDetalleSchema),
    /** Insight de concentración derivado (sobre volumen, no margen). null si no aplica. */
    insight: z.object({ texto: z.string(), tono: z.enum(['info', 'warn']) }).nullable(),
    /** DN10: base del dinero por cliente. */
    base_utilidad: z.literal('validada').optional(),
    /** DN10: pendiente de validar del mes, consolidado líneas + tiendas. null sin costos. */
    pendiente_validacion_utilidad: pendienteValidacionUtilidadSchema.nullable().optional(),
    actualizado_at: z.string().datetime(),
});
//# sourceMappingURL=directivo_analisis_cliente.js.map